// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import emailjs from '@emailjs/browser';

// 🆕 EmailJS Initialize
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
      storageKey: 'marshall_supabase_auth',
    },
  }
);

const API_BASE = process.env.REACT_APP_API_BASE || 'https://insightdraft-backend.onrender.com';
const GUEST_CREDIT_KEY = 'guest_credits';
const GUEST_CREDIT_LIMIT = 2;

// 🆕 EmailJS Config
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_OTP_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_OTP_TEMPLATE_ID;
const EMAILJS_WELCOME_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_WELCOME_TEMPLATE_ID;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Guest credits
  const getGuestCredits = () => {
    const stored = localStorage.getItem(GUEST_CREDIT_KEY);
    return stored !== null ? parseInt(stored, 10) : GUEST_CREDIT_LIMIT;
  };

  const [guestCredits, setGuestCreditsState] = useState(getGuestCredits);

  const setGuestCredits = (val) => {
    localStorage.setItem(GUEST_CREDIT_KEY, val);
    setGuestCreditsState(val);
  };

  // Check if JWT token is expired
  const isTokenExpired = (tok) => {
    try {
      const payload = JSON.parse(atob(tok.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch (e) {
      return true;
    }
  };

  // Try to refresh JWT token from backend
  const refreshToken = async () => {
    try {
      const refreshTok = localStorage.getItem('refreshToken');
      if (!refreshTok) return false;

      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refreshTok }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          localStorage.setItem('token', data.token);
          setToken(data.token);
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  // Load token from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      if (isTokenExpired(savedToken)) {
        console.log('Token expired on load, trying refresh...');
        refreshToken().then((refreshed) => {
          if (!refreshed) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          } else {
            setToken(localStorage.getItem('token'));
            setUser(JSON.parse(savedUser));
          }
          setLoading(false);
        });
      } else {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Auto-check token every 5 minutes
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      if (isTokenExpired(token)) {
        console.log('Token expired (periodic check), trying refresh...');
        refreshToken().then((refreshed) => {
          if (!refreshed) {
            logout();
          }
        });
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token]);

  // Save session
  const saveSession = (tok, userData, refreshTok = null) => {
    localStorage.setItem('token', tok);
    localStorage.setItem('user', JSON.stringify(userData));
    if (refreshTok) {
      localStorage.setItem('refreshToken', refreshTok);
    }
    setToken(tok);
    setUser(userData);
    window.dispatchEvent(new Event('authChange'));
  };

  // Clear session
  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    await supabase.auth.signOut();
    window.dispatchEvent(new Event('authChange'));
  };

  // 🆕 Send OTP Email via EmailJS (Client-side)
  const sendOTPEmailViaEmailJS = async (toEmail, otp) => {
    try {
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_OTP_TEMPLATE_ID,
        {
          to_email: toEmail,
          otp: otp,
          app_name: 'InsightDraft AI',
          year: new Date().getFullYear(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      return { success: true, data: result };
    } catch (error) {
      console.error('❌ EmailJS OTP error:', error.text || error.message);
      return { success: false, error: error.text || error.message };
    }
  };

  // 🆕 Send Welcome Email via EmailJS (Client-side)
  const sendWelcomeEmailViaEmailJS = async (toEmail, username) => {
    try {
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_WELCOME_TEMPLATE_ID,
        {
          to_email: toEmail,
          username: username || 'there',
          app_name: 'InsightDraft AI',
          frontend_url: process.env.REACT_APP_FRONTEND_URL || 'https://insightdraft-ai.onrender.com',
          year: new Date().getFullYear(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      return { success: true, data: result };
    } catch (error) {
      console.error('❌ EmailJS Welcome error:', error.text || error.message);
      return { success: false, error: error.text || error.message };
    }
  };

  // Manual Login
  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error);

    saveSession(data.token, data.user, data.refreshToken);
    return data;
  };

  // Google Login
  const loginWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  // Google callback
  const handleGoogleCallback = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw new Error('Failed to get session');
      if (!session) throw new Error('No session found');

      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: session.access_token
        }),
      });

      const data = await res.json();

      if (!data.success) throw new Error(data.error || 'Backend authentication failed');

      saveSession(data.token, data.user, data.refreshToken);
      return data;
    } catch (error) {
      throw error;
    }
  };

  // API helper with auto-retry on 401
  const authenticatedFetch = async (url, options = {}) => {
    if (!token) throw new Error('Please login');

    if (isTokenExpired(token)) {
      const refreshed = await refreshToken();
      if (!refreshed) {
        logout();
        throw new Error('Session expired. Please login again.');
      }
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        return fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });
      } else {
        logout();
        throw new Error('Session expired. Please login again.');
      }
    }

    return response;
  };

  // Credit System
  const availableCredits = user ? user.credits : guestCredits;
  const canUseCredit = () => availableCredits > 0;

  const deductCredit = useCallback(async () => {
    if (user && token) {
      setUser(prev => ({ ...prev, credits: Math.max(0, prev.credits - 1) }));
      localStorage.setItem('user', JSON.stringify({ ...user, credits: Math.max(0, user.credits - 1) }));
    } else {
      const newVal = Math.max(0, guestCredits - 1);
      setGuestCredits(newVal);
    }
  }, [user, token, guestCredits]);

  // 🆕 Send OTP - Backend generates OTP, Frontend sends email
  const sendOTP = async (email, username, password) => {
    // Step 1: Backend generates & saves OTP
    const res = await fetch(`${API_BASE}/api/auth/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password }),
    });

    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to send OTP');
    }

    // Step 2: Send OTP via EmailJS (client-side)
    if (data.dev_otp) {
      const emailResult = await sendOTPEmailViaEmailJS(email, data.dev_otp);
      
      if (!emailResult.success) {
        console.warn('⚠️ EmailJS failed. OTP available in console:', data.dev_otp);
        // Still return success — OTP is saved in DB
        return { 
          ...data, 
          otp_hint: data.dev_otp,
          message: 'OTP generated! Check console for OTP (EmailJS failed silently).'
        };
      }
    }

    return { 
      ...data, 
      otp_hint: data.dev_otp 
    };
  };

  // 🆕 Verify OTP + Signup + Send Welcome Email
  const verifyOTPAndSignup = async (email, username, password, otp) => {
    const res = await fetch(`${API_BASE}/api/auth/verify-otp-and-signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password, otp }),
    });
    
    const data = await res.json();
    
    if (!data.success) {
      throw new Error(data.error);
    }

    // 🆕 Send welcome email via EmailJS (fire & forget)
    sendWelcomeEmailViaEmailJS(email, username).then(result => {
      if (result.success) {
      } else {
        console.warn('⚠️ Welcome email failed:', result.error);
      }
    });

    return data;
  };

  // Save Analysis (with auto-retry)
  const saveAnalysis = async (type, title, analysisData) => {
    const res = await authenticatedFetch(`${API_BASE}/api/analyses/save`, {
      method: 'POST',
      body: JSON.stringify({ type, title, data: analysisData }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    return data.analysis;
  };

  // Get My Analyses (with auto-retry)
  const getMyAnalyses = async () => {
    const res = await authenticatedFetch(`${API_BASE}/api/analyses/my`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    return data.analyses;
  };

  const getAnalysis = async (id) => {
    const res = await authenticatedFetch(`${API_BASE}/api/analyses/${id}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    return data.analysis;
  };

  const deleteAnalysis = async (id) => {
    const res = await authenticatedFetch(`${API_BASE}/api/analyses/${id}`, {
      method: 'DELETE',
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    return data;
  };

  const value = {
    user,
    token,
    loading,
    isLoggedIn: !!user,
    availableCredits,
    guestCredits,
    canUseCredit,
    deductCredit,
    login,
    logout,
    loginWithGoogle,
    handleGoogleCallback,
    sendOTP,
    verifyOTPAndSignup,
    saveAnalysis,
    getMyAnalyses,
    getAnalysis,
    deleteAnalysis,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};